import {
    IndividualDocumentUploadEntitlement,
    NotifyIndividualDocumentUploadRequest,
    UpdateIndividualDocumentUploadEntitlementRequest,
} from "../../../models/IndividualDocumentUploadEntitlement";
import {
    CreateIndividualDocumentRequest,
    IndividualDocumentDecisionRequest,
    IndividualDocumentInternalNoteRequest,
    IndividualDocumentModel,
    IndividualDocumentVisibilityRequest,
    UploadIndividualDocumentRequest,
} from "../../../models/IndividualDocument";
import { Result } from "../../../models/Result";
import { httpService } from "../../HttpService";

// Review/decision/note/visibility/entitlement calls are small JSON requests: 30 s instead of the
// instance's 120 s. A timeout surfaces as a "reload and check the status" message in the caller.
const ACTION_TIMEOUT = { timeout: 30000 };

class IndividualDocumentApiService {
    private path: string = "IndividualDocument";

    getForIndividual = async (
        individualId: number,
        pageNumber: number = 1,
        pageSize: number = 50,
    ): Promise<Result<IndividualDocumentModel[]>> =>
        await httpService.get<IndividualDocumentModel[]>(
            `${this.path}/individual/${individualId}?pageNumber=${pageNumber}&pageSize=${pageSize}`,
            ACTION_TIMEOUT,
        );

    get = async (documentId: number): Promise<Result<IndividualDocumentModel>> =>
        await httpService.get<IndividualDocumentModel>(`${this.path}/${documentId}`);

    // Raw file bytes for Preview/Download; CRM reads File Storage itself. Own 30 s deadline instead of the
    // instance's 120 s; the signal lets a closed preview abort the request.
    getContent = async (documentId: number, signal?: AbortSignal): Promise<Result<ArrayBuffer>> =>
        await httpService.getBinary(`${this.path}/${documentId}/content`, { timeout: 30000, signal });

    // Metadata-only creation for a caller that already streamed the file to File Storage itself and
    // holds a FileStorageId (e.g. the customer channel once TNPDEVAPPS-1654 ships) — Portal does not
    // call this directly. For Officer/Approver upload from Merchant Portal, use upload() below.
    create = async (request: CreateIndividualDocumentRequest): Promise<Result<IndividualDocumentModel>> =>
        await httpService.post<IndividualDocumentModel>(`${this.path}`, request);

    // Officer/Approver upload (TNPDEVAPPS-1658) — Portal sends the file itself (base64); CRM stores
    // it via the existing non-restricted File Storage upload, then creates the metadata record.
    // 60 s deadline (a ~10 MB file is ~13 MB as base64) instead of the instance's 120 s; the signal backs Cancel.
    upload = async (request: UploadIndividualDocumentRequest, signal?: AbortSignal): Promise<Result<IndividualDocumentModel>> =>
        await httpService.post<IndividualDocumentModel>(`${this.path}/upload`, request, { timeout: 60000, signal });

    startReview = async (documentId: number, rowVersion: string): Promise<Result<IndividualDocumentModel>> =>
        await httpService.post<IndividualDocumentModel>(`${this.path}/${documentId}/start-review`, { rowVersion }, ACTION_TIMEOUT);

    confirm = async (
        documentId: number,
        request: IndividualDocumentDecisionRequest,
    ): Promise<Result<IndividualDocumentModel>> =>
        await httpService.post<IndividualDocumentModel>(`${this.path}/${documentId}/confirm`, request, ACTION_TIMEOUT);

    reject = async (
        documentId: number,
        request: IndividualDocumentDecisionRequest,
    ): Promise<Result<IndividualDocumentModel>> =>
        await httpService.post<IndividualDocumentModel>(`${this.path}/${documentId}/reject`, request, ACTION_TIMEOUT);

    updateInternalNote = async (
        documentId: number,
        request: IndividualDocumentInternalNoteRequest,
    ): Promise<Result<IndividualDocumentModel>> =>
        await httpService.put<IndividualDocumentModel>(`${this.path}/${documentId}/internal-note`, request, ACTION_TIMEOUT);

    updateCustomerVisibility = async (
        documentId: number,
        request: IndividualDocumentVisibilityRequest,
    ): Promise<Result<IndividualDocumentModel>> =>
        await httpService.put<IndividualDocumentModel>(`${this.path}/${documentId}/customer-visibility`, request, ACTION_TIMEOUT);

    getUploadEntitlement = async (individualId: number): Promise<Result<IndividualDocumentUploadEntitlement>> =>
        await httpService.get<IndividualDocumentUploadEntitlement>(`/${this.path}/individual/${individualId}/upload-entitlement`);

    updateUploadEntitlement = async (
        individualId: number,
        request: UpdateIndividualDocumentUploadEntitlementRequest,
    ): Promise<Result<IndividualDocumentUploadEntitlement>> =>
        await httpService.put<IndividualDocumentUploadEntitlement>(`/${this.path}/individual/${individualId}/upload-entitlement`, request, ACTION_TIMEOUT);

    notifyCustomer = async (individualId: number, request: NotifyIndividualDocumentUploadRequest): Promise<Result<void>> =>
        await httpService.post<void>(`/${this.path}/individual/${individualId}/notify-customer`, request, ACTION_TIMEOUT);
}

const individualDocumentApiService = new IndividualDocumentApiService();

export { individualDocumentApiService };
